import { useEffect, useRef, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Siren, Ambulance, Flame, MessageCircle } from "lucide-react";
import { toast } from "sonner";

export const EMERG_KEYS = { enabled: "hd-emerg-monitor-enabled", interval: "hd-emerg-monitor-interval" };

const TIPO_ICON = { panico: Siren, medica: Ambulance, incendio: Flame };
const TIPO_COR = { panico: "#ffb4ab", medica: "#7ef0a0", incendio: "#f4a261" };

function cfg() {
  return {
    enabled: localStorage.getItem(EMERG_KEYS.enabled) === "1",
    interval: Math.max(5, Number(localStorage.getItem(EMERG_KEYS.interval)) || 10),
  };
}

export default function EmergencyMonitor() {
  const [fila, setFila] = useState([]);
  const [aberto, setAberto] = useState(false);
  const [acking, setAcking] = useState(false);
  const pollingRef = useRef(false);

  useEffect(() => {
    let timer;
    let cancelado = false;
    const tick = async () => {
      if (cancelado) return;
      const c = cfg();
      if (c.enabled && !pollingRef.current) {
        pollingRef.current = true;
        try {
          const { data } = await api.get("/emergencias/pendentes");
          if (Array.isArray(data) && data.length) {
            setFila((prev) => {
              const ids = new Set(prev.map((e) => e.id));
              return [...prev, ...data.filter((e) => !ids.has(e.id))];
            });
          }
        } catch { /* polling em background: silencioso */ }
        finally { pollingRef.current = false; }
      }
      timer = setTimeout(tick, c.interval * 1000);
    };
    timer = setTimeout(tick, 1500);
    return () => { cancelado = true; clearTimeout(timer); };
  }, []);

  const ev = fila[0] || null;

  useEffect(() => {
    if (ev && !aberto) {
      setAberto(true);
      api.post(`/emergencias/${ev.id}/disparado`).catch(() => {});
    }
  }, [ev, aberto]);

  const fechar = async () => {
    if (!ev || acking) return;
    setAcking(true);
    try {
      await api.post(`/emergencias/${ev.id}/ack`);
      toast.success("Recebimento registrado");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setAcking(false);
      setAberto(false);
      setFila((f) => f.slice(1));
    }
  };

  const openWhats = () => {
    if (!ev?.telefone) return;
    const msg = encodeURIComponent(`Olá ${ev.nome}, recebemos seu alerta de ${ev.tipo_label} (unidade ${ev.eql}). Estamos à disposição.`);
    const w = 420, h = 720;
    const left = Math.max(0, window.screen.availWidth - w - 24);
    window.open(`https://wa.me/${ev.telefone}?text=${msg}`, "helpdesk-whatsapp",
      `popup=yes,width=${w},height=${h},left=${left},top=60`);
  };

  if (!ev) return null;
  const Icon = TIPO_ICON[ev.tipo] || Siren;
  const cor = TIPO_COR[ev.tipo] || "#ffb4ab";

  return (
    <Dialog open={aberto} onOpenChange={(o) => { if (o) setAberto(true); }}>
      <DialogContent className="bg-zinc-900 border-red-900/60 text-zinc-100 max-w-sm" data-testid="emergency-monitor-modal">
        <DialogHeader>
          <DialogTitle className="text-lg font-semibold text-red-400 flex items-center gap-2" data-testid="emergency-modal-title">
            <Icon className="h-5 w-5" style={{ color: cor }} /> Emergência Disparada
          </DialogTitle>
          <DialogDescription className="text-sm font-medium text-zinc-200" data-testid="emergency-modal-tipo">
            Tipo: {ev.tipo_label}
          </DialogDescription>
        </DialogHeader>
        <dl className="space-y-2 text-sm" data-testid="emergency-modal-dados">
          <div className="flex justify-between gap-3">
            <dt className="text-zinc-500">Unidade (EQL)</dt>
            <dd className="font-medium" data-testid="emergency-eql">{ev.eql}</dd>
          </div>
          <div className="flex justify-between gap-3">
            <dt className="text-zinc-500">Nome</dt>
            <dd className="font-medium text-right" data-testid="emergency-nome">{ev.nome}</dd>
          </div>
          <div className="flex justify-between gap-3">
            <dt className="text-zinc-500">E-mail</dt>
            <dd className="font-medium text-right break-all" data-testid="emergency-email">{ev.email || "-"}</dd>
          </div>
          <div className="flex justify-between items-center gap-3">
            <dt className="text-zinc-500">Telefone</dt>
            <dd className="font-medium flex items-center gap-2" data-testid="emergency-telefone">
              <span>{ev.telefone || "-"}</span>
              {ev.telefone && (
                <button type="button" data-testid="emergency-whatsapp-btn" onClick={openWhats} title="Abrir WhatsApp"
                  className="w-8 h-8 rounded-md flex items-center justify-center bg-emerald-600/20 text-emerald-400 hover:bg-emerald-600/30 transition-colors">
                  <MessageCircle className="h-4 w-4" />
                </button>
              )}
            </dd>
          </div>
        </dl>
        <Button data-testid="emergency-ack-btn" onClick={fechar} disabled={acking}
          className="w-full bg-red-600 hover:bg-red-500 text-white rounded-md mt-2 transition-colors">
          {acking ? "Registrando..." : "Confirmar recebimento"}
        </Button>
      </DialogContent>
    </Dialog>
  );
}
